/**
 * IP Intel Report — public landing page at /ip-intel-report.
 *
 * Built from the design mockup (Downloads/Landing page-html/Main.dc.html,
 * 2026-09-27): hero + "At a glance" card, the seven scope questions, trusted-by
 * row, sample reports with a lead-gated download, how it works, FAQ, bulk
 * strip and the dark CTA band. Values (colours, type, spacing, breakpoints at
 * 900 / 600 px) are the design's; they live in ipintel/styles.tsx, scoped.
 *
 * Every price on the page comes from GET /api/v1/ip-intel/config, which
 * resolves the visitor's region. No region → "Pricing for your region on
 * request" and every order button becomes a "Contact us" mail link.
 *
 * Tracking: every button is an `ipintel_*` trackClick (see ipintel/track.ts,
 * IPINTEL_EVENTS for the full list); the admin "IP Intel Report" tab reads
 * them back from site_events.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePageTitle } from '../../utils/pageTitle';
import { apiService } from '../../services/apiService';
import { IpIntelShell } from './ipintel/Shell';
import { IpIntelStyles } from './ipintel/styles';
import { IprModal, ModalCloseVia } from './ipintel/Modal';
import { PaymentSheet } from './ipintel/PaymentSheet';
import { SamplesSection } from './ipintel/SamplesSection';
import { BulkEnquiryModal } from './ipintel/BulkEnquiry';
import { captureSource, ipIntelApi, IpIntelConfig, IpIntelSample, VerifyResponse } from './ipintel/api';
import { BulkPosition, ipTrack, OrderPosition, setIpTrackContext, useScrollDepth, useSectionViews } from './ipintel/track';
import { usePageMeta } from './ipintel/seo';
import { usdAlt, withUsd } from './ipintel/usd';
import { CONTACT_EMAIL, FALLBACK_SAMPLES, FAQS, QUESTIONS, TRUSTED_BY } from './ipintel/content';

const CANONICAL = 'https://universitytech.io/ip-intel-report';
const META_DESCRIPTION =
  'The IP Intelligence Report: a seven-module commercial assessment of any university technology — '
  + 'market sizing, royalty benchmarks, competition, IP landscape, funding pathway and a go-to-market '
  + 'recommendation, delivered in seven days.';
/** The FAQ line that doubles as a way into the bulk-pricing form. */
const BULK_PHRASE = 'Contact us for bulk pricing.';

type ConfigState = 'loading' | 'ready' | 'error';

const scrollToId = (id: string) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/** The public route's tab title, meta description and canonical. Its own
 *  component so the in-platform tab (embedded) can leave the app's alone. */
const PublicHead: React.FC = () => {
  usePageTitle('IP Intel Report');
  usePageMeta({
    title: 'The IP Intelligence Report | UniversityTech.io',
    description: META_DESCRIPTION,
    canonical: CANONICAL,
    type: 'website',
  });
  return null;
};

/** What every ipintel_* event carries while the page is the platform's tab. */
const PLATFORM_TRACK = { surface: 'platform', entry: 'tab' };

interface Props {
  /**
   * Rendered as the signed-in platform's "IP Intel" tab (App.tsx view
   * 'ipintel'): no public nav / footer / auth modals, the app's tab title and
   * meta left alone, scroll depth read from the tab's own scroll container,
   * events and orders tagged {surface: 'platform', entry: 'tab'}, and a paid
   * order confirmed in place instead of leaving the app for the intake page.
   */
  embedded?: boolean;
}

interface PaidInfo { orderRef: string; intakeUrl: string; autoIntake: boolean; email: string }

const GreenCheck: React.FC = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" aria-hidden="true">
    <circle cx="22" cy="22" r="22" fill="var(--ipr-success-bg)" />
    <path d="M13.5 22.5l5.5 5.5 11.5-12" stroke="var(--ipr-success-400)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IpIntelReportPage: React.FC<Props> = ({ embedded = false }) => {
  // In the platform every event says so. Set during render as well as in the
  // effect: children's effects (and the first section-view callbacks) run
  // before this component's own, and must already carry it.
  if (embedded) setIpTrackContext(PLATFORM_TRACK);
  useEffect(() => {
    if (!embedded) return;
    setIpTrackContext(PLATFORM_TRACK);
    return () => setIpTrackContext(null);
  }, [embedded]);
  const scrollBox = useRef<HTMLDivElement>(null);
  const [paid, setPaid] = useState<PaidInfo | null>(null);
  const [config, setConfig] = useState<IpIntelConfig | null>(null);
  const [configState, setConfigState] = useState<ConfigState>('loading');
  const [payFrom, setPayFrom] = useState<OrderPosition | null>(null);
  const [bulkFrom, setBulkFrom] = useState<BulkPosition | null>(null);
  // The sample list shown: null until /config first answers (placeholder
  // cards), then the server's list — or the built-in one if /config fails
  // and nothing better has arrived. A later retry never blanks it again.
  const [samples, setSamples] = useState<IpIntelSample[] | null>(null);
  const viewTracked = useRef(false);

  // Admin preview: /ip-intel-report?as_country=US shows what a US visitor
  // sees. Passed through to /config only; the server decides whether to honour it.
  const asCountry = (() => {
    try { return new URLSearchParams(window.location.search).get('as_country'); } catch { return null; }
  })();

  const loadConfig = useCallback(async (attempt = 0) => {
    setConfigState('loading');
    const res = await ipIntelApi.getConfig(asCountry);
    if (res.ok) {
      setConfig(res.data);
      setConfigState('ready');
      setSamples(Array.isArray(res.data?.samples) ? res.data.samples : FALLBACK_SAMPLES);
      return res.data;
    }
    // One quiet retry for a flaky network before showing an error state.
    if (attempt === 0 && res.status !== 404) {
      await new Promise(r => setTimeout(r, 2500));
      return loadConfig(1);
    }
    setConfigState('error');
    setSamples(prev => prev ?? FALLBACK_SAMPLES);
    return null;
  }, [asCountry]);

  useEffect(() => {
    // First-touch UTM capture belongs to the public landing; inside the app
    // the order is attributed by surface/entry instead.
    if (!embedded) captureSource();
    void loadConfig().then(cfg => {
      if (viewTracked.current) return;
      viewTracked.current = true;
      ipTrack('ipintel_page_view', {
        available: cfg ? !!cfg.available : null,
        region: cfg?.region?.code || null,
        ...(asCountry ? { as_country: asCountry } : {}),
      });
    });
  }, [loadConfig, asCountry, embedded]);

  useSectionViews(true);
  useScrollDepth('landing', true, embedded ? scrollBox : undefined);

  const ready = configState === 'ready' && !!config;
  const available = ready && !!config!.available && !!config!.display;
  const unavailable = ready && !available;
  const contactHref = `mailto:${config?.contact_email || CONTACT_EMAIL}?subject=${encodeURIComponent('IP Intelligence Report — pricing for my region')}`;
  const turnaround = config?.turnaround_days || 7;

  const openOrder = (position: OrderPosition) => {
    ipTrack('ipintel_click_order', { position });
    setPayFrom(position);
    ipTrack('ipintel_payment_modal_open', { position });
  };

  const contact = (position: OrderPosition) => {
    ipTrack('ipintel_click_contact', { position, region: config?.region?.code || null });
    window.location.href = contactHref;
  };

  const openBulk = (position: BulkPosition) => {
    ipTrack('ipintel_click_bulk_pricing', { position });
    setBulkFrom(position);
    ipTrack('ipintel_bulk_modal_open', { position, signed_in: !!apiService.getStoredUserId?.() });
  };

  const retryConfig = (position: 'hero_card') => {
    ipTrack('ipintel_config_retry', { position });
    void loadConfig();
  };

  const seeSamples = (position: 'hero' | 'cta_band') => (e: React.MouseEvent) => {
    e.preventDefault();
    ipTrack('ipintel_click_see_samples', { position });
    scrollToId('samples');
  };

  /** An order button, or the "Contact us" it becomes where we do not sell.
   *  A render helper, not a component, so it is not remounted every render. */
  const orderButton = (position: OrderPosition, label: React.ReactNode, className = 'ipr-btn-primary', style?: React.CSSProperties) => (
    unavailable ? (
      <button type="button" className={className} style={style} onClick={() => contact(position)}>Contact us</button>
    ) : (
      <button type="button" className={className} style={style} onClick={() => openOrder(position)}>{label}</button>
    )
  );

  const skel = (w: number, h: number) => <span className="ipr-skel" style={{ width: w, height: h }} aria-label="Loading price" />;

  /** "₹40,000 + GST" as running text (step 01, dark band): `withPrice`
   *  follows the price; `noPrice` stands alone where there is none to show. */
  const priceText = (withPrice: string, noPrice: string) => {
    if (configState === 'loading') return <>{skel(110, 14)}{withPrice}</>;
    if (available) return <>{withUsd(config!.display!.price_with_tax_label, config)}{withPrice}</>;
    if (unavailable) return <>Pricing for your region on request. {noPrice}</>;
    return <>{noPrice}</>;
  };

  // A full page load, not react-router's navigate: a second react-router copy
  // (landing-page/node_modules on the build machine) has no <Router> above
  // this page, and its useNavigate threw on render in production (2026-09-27).
  // Inside the platform the buyer stays in the app: the confirmation shows
  // here, with the intake link (opened in a new tab) when one step is left.
  const onPaid = (intakeUrl: string, orderRef: string, result?: VerifyResponse) => {
    setPayFrom(null);
    // (An order for a known technology files its own intake: nothing to go to.)
    if (!embedded && intakeUrl && !result?.auto_intake) {
      window.location.href = intakeUrl;
      return;
    }
    const autoIntake = !!result?.auto_intake;
    setPaid({ orderRef, intakeUrl, autoIntake, email: String(result?.email || '') });
    ipTrack('ipintel_paid_modal_open', { order_ref: orderRef, auto_intake: autoIntake });
  };

  const closePaid = (via: ModalCloseVia | 'done' | 'later') => {
    if (paid) ipTrack('ipintel_paid_modal_close', { order_ref: paid.orderRef, via });
    setPaid(null);
  };

  const body = (
    <>
      {/* ============================ HERO ============================ */}
      <section className="ipr-section ipr-sec-hero">
        <div className="ipr-wrap ipr-hero">
          <div>
            <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 18 }}>Commercial diligence for Intellectual Property</div>
            <h1 className="ipr-h1">The IP Intelligence Report</h1>
            <p style={{ fontSize: 18, lineHeight: 1.6, color: 'var(--ipr-text)', maxWidth: 560, margin: '0 0 32px' }}>
              An in-depth commercial assessment of any technology offer on Cosmos — or of your own IP, uploaded. Seven modules that build confidence and evidence for negotiation and benchmarking.
            </p>
            <div className="ipr-cta-row">
              {orderButton('hero', 'Get your report')}
              <a className="ipr-btn-secondary" href="#samples" onClick={seeSamples('hero')}>See sample reports</a>
            </div>
            <a
              className="ipr-link-underline"
              href="#how-it-works"
              style={{ marginTop: 28 }}
              onClick={e => { e.preventDefault(); ipTrack('ipintel_click_how_it_works'); scrollToId('how-it-works'); }}
            >How it works</a>
          </div>

          <aside className="ipr-card ipr-hero-aside" style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="ipr-eyebrow" style={{ color: 'var(--ipr-muted)' }}>At a glance</div>
            <div>
              <div style={{ fontSize: 13, color: 'var(--ipr-muted)', marginBottom: 6 }}>Price</div>
              {configState === 'loading' ? (
                <div style={{ height: 34, display: 'flex', alignItems: 'center' }}>{skel(170, 28)}</div>
              ) : available ? (
                <div style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 28, fontWeight: 700, color: 'var(--ipr-heading)' }}>
                  {config!.display!.price}{' '}
                  <span style={{ fontSize: 15, fontWeight: 400, color: 'var(--ipr-text)' }}>+ {config!.tax_label || 'GST'}</span>
                  {/* The USD figure (display only — checkout stays in INR). */}
                  <div style={{ fontFamily: 'inherit', fontSize: 15, fontWeight: 500, color: 'var(--ipr-text)', marginTop: 4 }}>
                    {usdAlt(config)}
                  </div>
                </div>
              ) : unavailable ? (
                <div>
                  <div style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 20, fontWeight: 700, color: 'var(--ipr-heading)', lineHeight: 1.3 }}>
                    Pricing for your region on request
                  </div>
                  <a href={contactHref} onClick={() => ipTrack('ipintel_click_contact', { position: 'hero_card', region: config?.region?.code || null })}
                    style={{ fontSize: 14, fontWeight: 600, color: 'var(--ipr-primary-600)', textDecoration: 'underline' }}>
                    {config?.contact_email || CONTACT_EMAIL}
                  </a>
                </div>
              ) : (
                <div style={{ fontSize: 15, color: 'var(--ipr-heading)' }}>
                  Price unavailable right now.{' '}
                  <button type="button" className="ipr-linkbtn" onClick={() => retryConfig('hero_card')} style={{ color: 'var(--ipr-primary-600)', fontWeight: 600 }}>Retry</button>
                </div>
              )}
            </div>
            <div style={{ borderTop: '1px solid var(--ipr-border)', paddingTop: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--ipr-muted)', marginBottom: 6 }}>Turnaround</div>
              <div style={{ fontSize: 15, color: 'var(--ipr-heading)', fontWeight: 500 }}>{turnaround} days, subject to input clarifications</div>
            </div>
            <div style={{ borderTop: '1px solid var(--ipr-border)', paddingTop: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--ipr-muted)', marginBottom: 6 }}>Input</div>
              <div style={{ fontSize: 15, color: 'var(--ipr-heading)', fontWeight: 500 }}>A Cosmos listing or your own IP upload. TRL and stage of development are compulsory.</div>
            </div>
          </aside>
        </div>
      </section>

      {/* ============================ SCOPE ============================ */}
      <section id="scope" data-ipintel-section="scope" className="ipr-section ipr-sec-scope" style={{ background: 'var(--ipr-gray-50)' }}>
        <div className="ipr-wrap">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, marginBottom: 48, flexWrap: 'wrap' }}>
            <div>
              <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 12 }}>Scope</div>
              <h2 className="ipr-h2" style={{ maxWidth: 600 }}>Seven questions the report answers</h2>
            </div>
            <p style={{ fontSize: 16, color: 'var(--ipr-text)', maxWidth: 340, margin: 0 }}>
              One module per question. Each stands on its own evidence and feeds the recommendation at the end.
            </p>
          </div>
          <div className="ipr-q-grid">
            {QUESTIONS.map(q => (
              <div key={q.n} className="ipr-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 44 }}>
                <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-500)' }}>{q.n}</div>
                <div>
                  <p style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 18, fontWeight: 700, color: 'var(--ipr-heading)', lineHeight: 1.35, margin: '0 0 16px' }}>{q.q}</p>
                  <div style={{ borderTop: '1px solid var(--ipr-border)', paddingTop: 12, fontSize: 13, color: 'var(--ipr-muted)', fontWeight: 600 }}>{q.module}</div>
                </div>
              </div>
            ))}
            <div style={{
              // No warm drop shadow here either: it sat right under this
              // card's order button and read as the same halo (2026-09-27).
              background: 'var(--ipr-primary-100)', border: '1px solid var(--ipr-primary-300)', borderRadius: 12,
              boxShadow: 'none', padding: 24, display: 'flex', flexDirection: 'column',
              justifyContent: 'space-between', gap: 20,
            }}>
              <p style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 16, fontWeight: 500, color: 'var(--ipr-primary-700)', lineHeight: 1.5, margin: 0 }}>
                All seven modules, one technology, one document — delivered in {turnaround} days.
              </p>
              {orderButton(
                'scope_card',
                configState === 'loading' ? <>Order for {skel(96, 12)}</>
                  : available ? `Order for ${config!.display!.price_with_tax_label}` : 'Order a report',
                'ipr-btn-primary',
                { width: 'fit-content' },
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ========================== TRUSTED BY ========================== */}
      <section data-ipintel-section="trusted" className="ipr-section ipr-sec-trusted">
        <div className="ipr-wrap">
          <div className="ipr-eyebrow" style={{ color: 'var(--ipr-muted)', marginBottom: 24, textAlign: 'center' }}>Trusted by</div>
          <div className="ipr-trusted">
            {TRUSTED_BY.map(t => (
              <figure key={t.name} title={t.name}
                style={{ margin: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, minWidth: 96 }}>
                <img src={t.logo} alt={t.name} loading="lazy" decoding="async"
                  style={{ height: 56, width: 'auto', maxWidth: 180, objectFit: 'contain' }} />
                {/* The name in the type it had before the logos (owner, 2026-10-06). */}
                <figcaption style={{ fontFamily: 'var(--ipr-font-display)', fontWeight: 700, fontSize: 18, color: 'var(--ipr-gray-300)', textAlign: 'center', maxWidth: 220, lineHeight: 1.25 }}>{t.name}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ======================== SAMPLE REPORTS ======================== */}
      {/* The server's list once /config answers (admin-editable; may be
          empty if every sample is switched off); the built-in list only
          when /config could not be loaded at all. */}
      <SamplesSection loading={samples === null} samples={samples || []} sampleDelivery={config?.sample_delivery} />

      {/* ========================= HOW IT WORKS ========================= */}
      <section id="how-it-works" data-ipintel-section="how" className="ipr-section">
        <div className="ipr-wrap">
          <div className="ipr-how-head">
            <div>
              <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 12 }}>How it works</div>
              <h2 className="ipr-h2">Order to readout in seven days</h2>
            </div>
            <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ipr-text)', margin: 0, alignSelf: 'center' }}>
              The clock starts once the technology inputs are complete. TRL and stage of development are compulsory — without them the analysis cannot be benchmarked.
            </p>
          </div>
          <div className="ipr-how-steps">
            {[
              { n: '01', t: 'Pay to order', body: priceText(' through secure checkout. One technology per report.', 'One technology per report.') },
              { n: '02', t: 'Submit the technology', body: <>Once payment is confirmed, you're taken straight to the intake form: a Cosmos listing or your own IP, with TRL and stage of development declared.</> },
              { n: '03', t: 'Clarifications, if any', body: <>We come back with questions where the brief is thin. Turnaround is subject to those answers.</> },
              { n: '04', t: 'Report delivered', body: <>All seven modules in one document, with sources and assumptions stated throughout.</> },
            ].map((s, i) => (
              <div key={s.n}>
                <div style={{ height: 3, width: 40, background: i === 0 ? 'var(--ipr-primary-500)' : 'var(--ipr-gray-100)', marginBottom: 16 }} />
                <div className="ipr-eyebrow" style={{ color: 'var(--ipr-muted)', marginBottom: 8 }}>Step {s.n}</div>
                <div style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 18, fontWeight: 700, color: 'var(--ipr-heading)', marginBottom: 8 }}>{s.t}</div>
                <p style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--ipr-text)', margin: 0 }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================= FAQ ============================= */}
      <section id="faq" data-ipintel-section="faq" className="ipr-section" style={{ background: 'var(--ipr-gray-50)' }}>
        <div className="ipr-wrap ipr-faq-grid">
          <h2 className="ipr-h2">Before you order</h2>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {FAQS.map((f, i) => (
              <div key={f.q} style={{ borderBottom: i < FAQS.length - 1 ? '1px solid var(--ipr-border)' : 'none', padding: '20px 0' }}>
                <div style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 18, fontWeight: 700, color: 'var(--ipr-heading)', marginBottom: 8 }}>{f.q}</div>
                <p style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--ipr-text)', margin: 0 }}>
                  {f.a.includes(BULK_PHRASE) ? (
                    <>
                      {f.a.slice(0, f.a.indexOf(BULK_PHRASE))}
                      <button type="button" className="ipr-linkbtn" onClick={() => openBulk('faq')}
                        style={{ color: 'var(--ipr-primary-600)', fontWeight: 600 }}>{BULK_PHRASE}</button>
                      {f.a.slice(f.a.indexOf(BULK_PHRASE) + BULK_PHRASE.length)}
                    </>
                  ) : f.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= BULK STRIP ========================= */}
      <section data-ipintel-section="bulk" className="ipr-strip">
        <div className="ipr-wrap">
          <span style={{ fontSize: 15, color: 'var(--ipr-primary-700)' }}>Have more than one technology to assess? </span>
          <button
            type="button"
            className="ipr-linkbtn"
            aria-haspopup="dialog"
            onClick={() => openBulk('strip')}
            style={{ fontSize: 15, fontWeight: 700, color: 'var(--ipr-primary-700)' }}
          >Contact us for bulk pricing.</button>
        </div>
      </section>

      {/* ========================== CTA BAND ========================== */}
      <section data-ipintel-section="cta" className="ipr-band">
        <div className="ipr-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' }}>
          <div style={{ maxWidth: 520 }}>
            <h2 className="ipr-h2" style={{ lineHeight: 1.2, color: '#fff', margin: '0 0 12px' }}>Put a number on the technology</h2>
            <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ipr-gray-200)', margin: 0 }}>
              {priceText('. Seven modules, seven days, one document you can negotiate from.', 'Seven modules, seven days, one document you can negotiate from.')}
            </p>
          </div>
          <div className="ipr-cta-row">
            {orderButton('cta_band', 'Order a report')}
            <a className="ipr-btn-ghost" href="#samples" onClick={seeSamples('cta_band')}>See sample reports</a>
          </div>
        </div>
      </section>

      {payFrom && (
        <PaymentSheet
          config={config}
          configState={configState}
          onRetryConfig={() => void loadConfig()}
          position={payFrom}
          onClose={() => setPayFrom(null)}
          onPaid={onPaid}
          source={embedded ? PLATFORM_TRACK : undefined}
        />
      )}
      {bulkFrom && <BulkEnquiryModal position={bulkFrom} onClose={() => setBulkFrom(null)} />}
      {paid && (
        <IprModal labelledBy="ipr-paid-title" onClose={via => closePaid(via)} maxWidth={460}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14 }}>
            <GreenCheck />
            <div id="ipr-paid-title" style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 22, fontWeight: 700, color: 'var(--ipr-heading)' }}>
              Payment received
            </div>
            {paid.autoIntake || !paid.intakeUrl ? (
              <p style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--ipr-text)', margin: 0 }}>
                Your IP Intel Report will be sent to your registered email{paid.email ? <>, <b>{paid.email}</b>,</> : ''} within {turnaround} days.
              </p>
            ) : (
              <>
                <p style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--ipr-text)', margin: 0 }}>
                  One more step: tell us which technology to assess — a Cosmos listing or your own IP, with its TRL and
                  stage of development. Your {turnaround}-day clock starts when you submit it.
                </p>
                <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--ipr-gray-300)', margin: 0 }}>
                  We have also e-mailed the link{paid.email ? <> to <b>{paid.email}</b></> : ''}. Order {paid.orderRef}.
                </p>
              </>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', marginTop: 6 }}>
              {!paid.autoIntake && paid.intakeUrl ? (
                <>
                  <a className="ipr-btn-primary" href={paid.intakeUrl} target="_blank" rel="noopener noreferrer"
                    onClick={() => ipTrack('ipintel_paid_intake_click', { order_ref: paid.orderRef })}>
                    Complete your intake
                  </a>
                  <button type="button" className="ipr-btn-secondary" style={{ boxShadow: 'none' }} onClick={() => closePaid('later')}>
                    Later
                  </button>
                </>
              ) : (
                <button type="button" className="ipr-btn-primary" onClick={() => closePaid('done')}>Done</button>
              )}
            </div>
          </div>
        </IprModal>
      )}
    </>
  );

  if (embedded) {
    return (
      <div ref={scrollBox} className="ipr-embedded flex-1 min-w-0 self-stretch overflow-y-auto lg:rounded-2xl bg-white lg:shadow-custom-light">
        <div className="ipr">
          <IpIntelStyles />
          {/* No sticky site nav in the app: anchors need no 88px offset, and
              the page fills the tab rather than a whole viewport. */}
          <style>{'.ipr-embedded .ipr{min-height:100%}.ipr-embedded .ipr [id]{scroll-margin-top:12px}'}</style>
          <main>{body}</main>
        </div>
      </div>
    );
  }

  return (
    <>
      <PublicHead />
      <IpIntelShell
        onSelf={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        rightAction={
          orderButton('header', 'Order a report', 'ipr-btn-primary ipr-nav-order')
        }
      >
        {body}
      </IpIntelShell>
    </>
  );
};

export default IpIntelReportPage;
